import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslationApp } from "../../../../lib/i18next";
import { Controller, useController, useFormContext } from "react-hook-form";
import { COMING_SOON, FIELD_REQUIRED } from "../../../../i18n/keysTranslations";
import {
  GOOGLE,
  IDS_PLATFORM_BUTTONS,
  LINKEDIN,
  META,
  PLATFORMS_SUPPORTED_WITH_COMING_SOON,
  TIKTOK,
} from "../../../../utils/constants";
import { useCreditActionCosts } from "../../../../hooks/useCreditActionCosts";
import ButtonMetaAction from "../../../../components/Buttons/ButtonMetaAction";
import ButtonGoogleAction from "../../../../components/Buttons/ButtonGoogleAction";
import ButtonLinkedinAction from "../../../../components/Buttons/ButtonLinkedinAction";
import ButtonTikTokBusinessAction from "../../../../components/Buttons/ButtonTikTokBusinessAction";
import React, { useState } from "react";
import GenerateCampaignMeta from "../../../../components/Platforms/Meta/GenerateCampaign/GenerateCampaignMeta";
import GenerateCampaignTikTok from "../../../../components/Platforms/TikTok/GenerateCampaign/GenerateCampaignTikTok";
import GenerateCampaignGoogle from "../../../../components/Platforms/Google/GenerateCampaign/GenerateCampaignGoogle";
import GenerateCampaignLinkedin from "../../../../components/Platforms/Linkedin/GenerateCampaign/GenerateCampaignLinkedin";
import { getInitialPlatformSetupData } from "../../utils/getInitialPlatformSetupData";
import Avatar from "@mui/material/Avatar";
import { logoPlatform } from "../../../../utils/string";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Add } from "@mui/icons-material";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";
import { getIsBlockedActions } from "../../../../actions/getters";
import { useDispatchApp, useSelectorApp } from "../../../../lib/redux";
import {
  CAMPAIGN_BILLING_MODE_PER_CHANNEL,
  resolveCampaignBillingModeForVersion,
} from "../../../../utils/creditActionCosts";
import Button from "../../../../components/Buttons/Button";
import _ from "lodash";

const GENERATE_CAMPAIGN_FORMS = {
  [META]: <GenerateCampaignMeta />,
  [TIKTOK]: <GenerateCampaignTikTok />,
  [GOOGLE]: <GenerateCampaignGoogle />,
  [LINKEDIN]: <GenerateCampaignLinkedin />,
};

const AddPlatformsCampaign = ({
  typeView = "default",
  label,
  disabledButtons,
  sxButtons = {},
  filterPlatforms = [],
  className = "",
}) => {
  const [initialData, setInitialData] = useState(null);
  const [modalPlatformSetupOpen, setModalPlatformSetupOpen] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslationApp();
  const { ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION } = useCreditActionCosts();

  const creditPricingVersion = useSelectorApp(
    (state) => state?.payments?.creditPricingVersion
  );
  const isTrial = useSelectorApp(
    (state) => state?.payments?.subscriptionPlanInfo?.isTrial
  );

  const {
    control,
    trigger,
    formState: { errors },
    isGenerating,
    onGenerateCampaignPlatform,
    campaignID,
    getValues,
    watch,
    brandInformation,
    onChangeIsForcedSetupPage,
    onChangeSectionSelected,
    getIsPlatformOptionDisabled,
    isGeneralCampaignIncompatible,
  } = useFormContext();
  const platformField = useController({
    name: "platforms",
    control,
  });
  const locations = watch("locations");
  const language = watch("language");
  const context = watch("context");
  const platforms = watch("platforms");
  const generatedPlatforms = watch("generatedPlatforms") || {};
  const chargesPerChannel =
    resolveCampaignBillingModeForVersion(creditPricingVersion) ===
    CAMPAIGN_BILLING_MODE_PER_CHANNEL;
  // First platform: always verify. Additional platforms: only when each channel is billed (v3).
  const verifyBlockAlert = _.isEmpty(platforms) || chargesPerChannel;

  const dispatch = useDispatchApp();

  const getConfigurationPlatform = (platform) => {
    if (platform === META) {
      return {
        id: IDS_PLATFORM_BUTTONS[platform],
        container: <ButtonMetaAction />,
      };
    }
    if (platform === TIKTOK) {
      return {
        id: IDS_PLATFORM_BUTTONS[platform],
        container: <ButtonTikTokBusinessAction />,
      };
    }
    if (platform === GOOGLE) {
      return {
        id: IDS_PLATFORM_BUTTONS[platform],
        container: <ButtonGoogleAction />,
      };
    }
    if (platform === LINKEDIN) {
      return {
        id: IDS_PLATFORM_BUTTONS[platform],
        container: <ButtonLinkedinAction />,
      };
    }
  };

  const onCallbackSavePlatform = (platformData) => {
    onGenerateCampaignPlatform(platformData);
    setModalPlatformSetupOpen(null);
  };
  const onSelectPlatform = async (platform) => {
    if (platformField.field.value.includes(platform)) {
      platformField.field.onChange(
        platformField.field.value.filter((v) => v !== platform)
      );
      return;
    }

    const contextValid = await trigger(["context", "language", "locations"]);

    if (!contextValid) {
      if (platformField.field.value.length > 0) {
        onChangeIsForcedSetupPage(true);
        onChangeSectionSelected({
          platform: undefined,
          adGroupID: undefined,
          adID: undefined,
          from: "addPlatforms",
        });
      }
      return;
    }

    if (isTrial) {
      const isBlocked = await dispatch(
        getIsBlockedActions({
          action: ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION,
          showUsageConfirmationAlert: false,
          campaignUsageContext: {
            platform,
            generatedPlatforms,
          },
        })
      );
      if (isBlocked) {
        return;
      }
    }

    await getInitialDataByPlatform(platform);
    setModalPlatformSetupOpen(platform);
  };

  const getInitialDataByPlatform = async (platform) => {
    const platforms = getValues("platforms");
    const urlParams = new URLSearchParams(window.location.search);
    const initialCreatives = urlParams.get("initialCreatives");
    const nextInitialData = await getInitialPlatformSetupData({
      platform,
      isFirstPlatform: platforms?.length === 0,
      brandInformation,
      initialCreatives,
      getValues,
    });
    setInitialData(nextInitialData);
    return nextInitialData;
  };

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  const handleSelectPlatform = async (platform) => {
    await onSelectPlatform(platform);
    handleCloseMenu();
  };

  const hideAddPlatforms =
    platforms?.length > 0 && isGeneralCampaignIncompatible;

  if (hideAddPlatforms) {
    return null;
  }

  return (
    <>
      {typeView === "sidebar" && (
        <Box>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleOpenMenu}
            startIcon={<Add />}
            fullWidth
          >
            {t(label)}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            PaperProps={{
              sx: {
                width: anchorEl ? anchorEl.offsetWidth : undefined,
              },
            }}
          >
            {PLATFORMS_SUPPORTED_WITH_COMING_SOON.map((platform) => {
              if (filterPlatforms.includes(platform)) return null;
              const isSelected = platformField.field.value.includes(platform);
              const isPlatformDisabled =
                typeof getIsPlatformOptionDisabled === "function"
                  ? getIsPlatformOptionDisabled(platform)
                  : false;
              return (
                <MenuItem
                  key={platform}
                  selected={isSelected}
                  onClick={() => handleSelectPlatform(platform)}
                  disabled={
                    isGenerating || disabledButtons || isPlatformDisabled
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    ...sxButtons,
                  }}
                >
                  <Avatar
                    alt={platform}
                    src={logoPlatform(platform)}
                    sx={{
                      width: 20,
                      height: 20,
                      mr: 1,
                      ".MuiAvatar-img": { objectFit: "contain" },
                    }}
                    variant="square"
                  />
                  <Typography sx={{ flex: 1 }}>{t(platform)}</Typography>
                </MenuItem>
              );
            })}
          </Menu>
          {errors.platforms && (
            <Typography variant="body2" color="error">
              {t(errors.platforms.message)}
            </Typography>
          )}
          {Boolean(modalPlatformSetupOpen) &&
            React.cloneElement(
              GENERATE_CAMPAIGN_FORMS[modalPlatformSetupOpen],
              {
                modalOpen: Boolean(modalPlatformSetupOpen),
                onCloseModal: () => setModalPlatformSetupOpen(null),
                campaignID,
                generatedPlatforms,
                context: {
                  locations,
                  language,
                  campaignContext: context,
                },
                onCallbackSave: onCallbackSavePlatform,
                initialData,
                verifyBlockAlert,
              }
            )}
        </Box>
      )}

      {typeView === "default" && (
        <Box className={className}>
          <Controller
            name="platforms"
            control={control}
            rules={{
              required: {
                value: true,
                message: t(FIELD_REQUIRED),
              },
            }}
            render={() => {
              return (
                <Box
                  sx={{
                    display: { xs: "grid", sm: "grid", md: "flex" },
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    flexDirection: { md: "row" },
                    flexWrap: { md: "nowrap" },
                    gap: 1,
                    width: "100%",
                    alignItems: "stretch",
                  }}
                >
                  {PLATFORMS_SUPPORTED_WITH_COMING_SOON.map((platform) => {
                    if (filterPlatforms.includes(platform)) return null;

                    const configurationPlatform =
                      getConfigurationPlatform(platform);
                    const isSelected =
                      platformField.field.value.includes(platform);

                    return (
                      <Box
                        sx={{
                          width: { xs: "100%", sm: "100%", md: "auto" },
                          flex: { md: "1 1 0" },
                          minWidth: 0,
                        }}
                        key={platform}
                      >
                        {React.cloneElement(configurationPlatform.container, {
                          id: configurationPlatform.id,
                          text: t(platform),
                          logo: platform === META ? META : undefined,
                          doTranslation: false,
                          onClick: () => onSelectPlatform(platform),
                          disabled: isGenerating || disabledButtons,
                          selected: isSelected,
                          sx: {
                            ...sxButtons,
                            color: "black",
                            borderRadius: 2,
                            backgroundColor: "transparent",
                            border: `1px solid ${INPUT_COLOR_BORDER}`,
                            boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                            width: "100%",
                            "&:hover": {
                              backgroundColor: isSelected
                                ? `white !important`
                                : "oklch(.95 0 0)",
                              color: isSelected
                                ? `black !important`
                                : `black !important`,
                            },
                            background: "unset",
                          },
                        })}
                        {configurationPlatform.isComingSoon && (
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.5,
                              color: "GrayText",
                              fontStyle: "italic",
                            }}
                          >
                            {t(COMING_SOON)}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            }}
          />
          {errors.platforms && (
            <Typography variant="body2" color="error">
              {t(errors.platforms.message)}
            </Typography>
          )}
          {Boolean(modalPlatformSetupOpen) &&
            React.cloneElement(
              GENERATE_CAMPAIGN_FORMS[modalPlatformSetupOpen],
              {
                modalOpen: Boolean(modalPlatformSetupOpen),
                onCloseModal: () => setModalPlatformSetupOpen(null),
                campaignID,
                generatedPlatforms,
                context: {
                  locations,
                  language,
                  campaignContext: context,
                },
                verifyBlockAlert,
                onCallbackSave: onCallbackSavePlatform,
                initialData,
              }
            )}
        </Box>
      )}
    </>
  );
};

export default AddPlatformsCampaign;
